import { IText } from 'fabric';

type FabricObjectWithCircularWarp = IText & {
    isCircularWarped?: boolean;
    _render_original_circular?: (ctx: CanvasRenderingContext2D) => void;
    circularWarpOptions?: {
        radius?: number;
        rotations?: number;
        rotationOffset?: number;
    }
};

function customCircularRender(this: FabricObjectWithCircularWarp, ctx: CanvasRenderingContext2D) {
    if (!this.isCircularWarped || !this.circularWarpOptions || !this._render_original_circular) {
        this._render_original_circular?.(ctx);
        return;
    }
    
    const {
        radius = 60,
        rotations = 1,
        rotationOffset = 0,
    } = this.circularWarpOptions;

    const text = this.text || '';
    const totalChars = text.length;
    if (totalChars === 0) return;

    const style = this.getCompleteStyleDeclaration(0, 0);
    ctx.font = this.fontStyle + ' ' + this.fontWeight + ' ' + this.fontSize + 'px ' + (this.fontFamily || 'sans-serif');
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = style.fill as string;
    
    const hasStroke = this.stroke && this.strokeWidth && this.strokeWidth > 0;
    if (hasStroke) {
        ctx.strokeStyle = this.stroke as string;
        ctx.lineWidth = this.strokeWidth || 1;
        ctx.lineJoin = 'round';
        ctx.miterLimit = 2;
    }
    
    const angleStep = (rotations * Math.PI * 2) / totalChars;

    ctx.save();
    for(let i = 0; i < totalChars; i++){
        const char = text[i];
        const angle = i * angleStep + rotationOffset;

        const x = radius * Math.cos(angle);
        const y = radius * Math.sin(angle);

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle + Math.PI / 2);
        if (hasStroke) {
            ctx.strokeText(char, 0, 0);
        }
        ctx.fillText(char, 0, 0);
        ctx.restore();
    }
    ctx.restore();
}


export function enableCircularWarp(textObject: IText, options?: { radius?: number, rotations?: number, rotationOffset?: number }) {
    const obj = textObject as FabricObjectWithCircularWarp;
    
    if (!obj.isCircularWarped) {
        obj._render_original_circular = obj._render.bind(obj);
        obj._render = customCircularRender as any;
    }
    
    obj.isCircularWarped = true;
    const current = obj.circularWarpOptions || {};
    obj.circularWarpOptions = {
        radius: options?.radius ?? current.radius ?? 60,
        rotations: options?.rotations ?? current.rotations ?? 0.7,
        rotationOffset: options?.rotationOffset ?? current.rotationOffset ?? 0,
    };
    obj.objectCaching = false;
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}

export function disableCircularWarp(textObject: IText) {
    const obj = textObject as FabricObjectWithCircularWarp;

    if (obj._render_original_circular) {
        obj._render = obj._render_original_circular;
        delete obj._render_original_circular;
    }

    obj.isCircularWarped = false;
    delete obj.circularWarpOptions;
    
    obj.objectCaching = true;
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}
